import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, Switch, FormControlLabel, Alert, Stack,
  Typography, Chip, Divider, Box, Autocomplete, CircularProgress,
  IconButton, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as userApi from '../../lib/api/user';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';

const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const VERSION_REGEX = /^\d+\.\d+\.\d+$/;

// 요일 CSV(NOTI_DAYS) <-> 칩 — 백엔드 AgentOfflineNotiSupport와 코드를 맞춘다(MON..SUN)
const DAY_OPTIONS = [
  { code: 'MON', label: '월' },
  { code: 'TUE', label: '화' },
  { code: 'WED', label: '수' },
  { code: 'THU', label: '목' },
  { code: 'FRI', label: '금' },
  { code: 'SAT', label: '토' },
  { code: 'SUN', label: '일' },
];

const validationSchema = Yup.object({
  agentNm: Yup.string().required('이름은 필수입니다.'),
  notiStartHm: Yup.string().matches(HHMM_REGEX, 'HH:mm 형식이어야 합니다.'),
  notiEndHm: Yup.string()
    .matches(HHMM_REGEX, 'HH:mm 형식이어야 합니다.')
    .test('after-start', '종료 시각은 시작 시각보다 늦어야 합니다.', function endsAfterStart(value) {
      const { notiStartHm } = this.parent;
      if (!HHMM_REGEX.test(value || '') || !HHMM_REGEX.test(notiStartHm || '')) return true;
      return value > notiStartHm;
    }),
  notiLateStartMin: Yup.number()
    .typeError('숫자를 입력하세요.')
    .min(0, '0 이상이어야 합니다.')
    .max(240, '240 이하여야 합니다.'),
  targetVer: Yup.string()
    .test('version-format', 'x.y.z 형식이어야 합니다(예: 0.4.1).', (value) => !value || VERSION_REGEX.test(value)),
});

// 등록/수정 겸용 다이얼로그. 토큰 재발급·삭제는 window.confirm 대신 다이얼로그 안에서
// 경고문 + 실행/취소 버튼으로 확인한다(모바일 웹뷰에서 window.confirm 이 막히는 경우가 있었다).
const EqpAgentFormDialog = ({
  open, agentData, onClose, onSave, onReissueToken, onDelete,
}) => {
  const isNew = !agentData;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmAction, setConfirmAction] = useState(null); // 'reissue' | 'delete' | null
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);

  useEffect(() => {
    if (open) setConfirmAction(null);
  }, [open]);

  // 뒤로가기(Android)로 닫기: 확인창이 history 스택 위에 따로 쌓이므로, 뒤로가기를 누르면
  // 확인창 → 수정/등록창 순서로 한 단계씩 닫힌다.
  useCloseOnBack(open, onClose);
  useCloseOnBack(Boolean(confirmAction), () => setConfirmAction(null));

  // 오프라인 알림 추가 수신자 선택용 - 수정(기존 에이전트) 다이얼로그가 열릴 때만 사용자 목록을 가져온다
  useEffect(() => {
    if (!open || isNew) return;
    let active = true;
    (async () => {
      try {
        setUserOptionsLoading(true);
        const res = await userApi.getUsers();
        if (active) setUserOptions(res.data?.list || []);
      } catch (e) {
        console.error('사용자 목록 조회 실패:', e);
      } finally {
        if (active) setUserOptionsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, isNew]);

  const formik = useFormik({
    initialValues: {
      agentNm:  agentData?.agentNm  || '',
      location: agentData?.location || '',
      useYn:    agentData?.useYn    || 'Y',
      rmk:      agentData?.rmk      || '',
      offlineNotiYn:      agentData?.offlineNotiYn      || 'Y',
      notiDays:           agentData?.notiDays           || 'MON,TUE,WED,THU,FRI',
      notiStartHm:        agentData?.notiStartHm        || '08:00',
      notiEndHm:          agentData?.notiEndHm          || '20:00',
      notiSkipHolidayYn:  agentData?.notiSkipHolidayYn  || 'Y',
      notiLateStartMin:   agentData?.notiLateStartMin ?? 30,
      recoverNotiYn:      agentData?.recoverNotiYn      || 'Y',
      notiUsrUuids:       agentData?.notiUsrUuids       || [],
      autoUpdateYn:       agentData?.autoUpdateYn       || 'Y',
      targetVer:          agentData?.targetVer          || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => onSave(values, agentData),
  });

  const selectedDays = (formik.values.notiDays || '').split(',').filter(Boolean);
  const toggleDay = (code) => {
    const next = selectedDays.includes(code)
      ? selectedDays.filter((d) => d !== code)
      : [...selectedDays, code];
    // 화면에 보이는 순서(월~일)대로 다시 정렬해 저장 — 어느 순서로 클릭해도 CSV가 흔들리지 않게
    const ordered = DAY_OPTIONS.map((d) => d.code).filter((c) => next.includes(c));
    formik.setFieldValue('notiDays', ordered.join(','));
  };

  const runConfirm = async () => {
    if (confirmAction === 'reissue') await onReissueToken(agentData);
    if (confirmAction === 'delete') await onDelete(agentData);
    setConfirmAction(null);
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{isNew ? '에이전트 등록' : '에이전트 수정'}</Box>
        <IconButton aria-label="닫기" onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="이름 *" name="agentNm" size="small"
              value={formik.values.agentNm}
              onChange={formik.handleChange}
              error={formik.touched.agentNm && Boolean(formik.errors.agentNm)}
              helperText={formik.touched.agentNm && formik.errors.agentNm}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="위치" name="location" size="small"
              value={formik.values.location}
              onChange={formik.handleChange}
            />
          </Grid>

          {!isNew && (
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.useYn === 'Y'}
                    onChange={(e) => formik.setFieldValue('useYn', e.target.checked ? 'Y' : 'N')}
                  />
                }
                label="사용"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth label="비고" name="rmk" size="small"
              multiline rows={2}
              value={formik.values.rmk}
              onChange={formik.handleChange}
            />
          </Grid>

          {!isNew && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>오프라인 알림</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                이 에이전트의 연결이 끊기거나 복구되면, 등록자와 담당 장비 관리자에게 알립니다.
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.offlineNotiYn === 'Y'}
                        onChange={(e) => formik.setFieldValue('offlineNotiYn', e.target.checked ? 'Y' : 'N')}
                      />
                    }
                    label="오프라인 알림 사용"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.recoverNotiYn === 'Y'}
                        onChange={(e) => formik.setFieldValue('recoverNotiYn', e.target.checked ? 'Y' : 'N')}
                      />
                    }
                    label="복구 알림 사용"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    알림 요일
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {DAY_OPTIONS.map((d) => (
                      <Chip
                        key={d.code}
                        label={d.label}
                        size="small"
                        clickable
                        color={selectedDays.includes(d.code) ? 'primary' : 'default'}
                        variant={selectedDays.includes(d.code) ? 'filled' : 'outlined'}
                        onClick={() => toggleDay(d.code)}
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth label="시작 시각" name="notiStartHm" size="small"
                    placeholder="08:00"
                    value={formik.values.notiStartHm}
                    onChange={formik.handleChange}
                    error={formik.touched.notiStartHm && Boolean(formik.errors.notiStartHm)}
                    helperText={formik.touched.notiStartHm && formik.errors.notiStartHm}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth label="종료 시각" name="notiEndHm" size="small"
                    placeholder="20:00"
                    value={formik.values.notiEndHm}
                    onChange={formik.handleChange}
                    error={formik.touched.notiEndHm && Boolean(formik.errors.notiEndHm)}
                    helperText={formik.touched.notiEndHm && formik.errors.notiEndHm}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.notiSkipHolidayYn === 'Y'}
                        onChange={(e) => formik.setFieldValue('notiSkipHolidayYn', e.target.checked ? 'Y' : 'N')}
                      />
                    }
                    label="공휴일 제외"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" type="number"
                    name="notiLateStartMin"
                    label="근무 시작 후 지나도 꺼져 있으면 알림(분)"
                    value={formik.values.notiLateStartMin}
                    onChange={formik.handleChange}
                    error={formik.touched.notiLateStartMin && Boolean(formik.errors.notiLateStartMin)}
                    helperText={
                      (formik.touched.notiLateStartMin && formik.errors.notiLateStartMin)
                      || '알림창 밖에서 끊기면 조용히 있다가, 알림 시작 시각 이후 이 시간이 지나도 계속 꺼져 있을 때만 1번 알립니다.'
                    }
                    inputProps={{ min: 0, max: 240 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={userOptions}
                    loading={userOptionsLoading}
                    getOptionLabel={(option) => option.usrNm || ''}
                    isOptionEqualToValue={(option, value) => option.usrUuid === value.usrUuid}
                    value={userOptions.filter((u) => (formik.values.notiUsrUuids || []).includes(u.usrUuid))}
                    onChange={(e, newValue) => formik.setFieldValue('notiUsrUuids', newValue.map((u) => u.usrUuid))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="알림 받을 사람 (추가)"
                        placeholder="이름으로 검색..."
                        helperText="등록자와 연결된 장비 담당자에게는 자동으로 보내고, 여기서 고른 사람에게도 함께 보냅니다."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {userOptionsLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {!isNew && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>자동 업데이트</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                에이전트 코드(ksm_agent)만 자동으로 받아 적용합니다. 실패하면 이전 버전으로 자동 복구됩니다.
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.autoUpdateYn === 'Y'}
                        onChange={(e) => formik.setFieldValue('autoUpdateYn', e.target.checked ? 'Y' : 'N')}
                      />
                    }
                    label="자동 업데이트 사용"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small" name="targetVer"
                    label="버전 고정"
                    placeholder="비우면 항상 최신"
                    value={formik.values.targetVer}
                    onChange={formik.handleChange}
                    error={formik.touched.targetVer && Boolean(formik.errors.targetVer)}
                    helperText={formik.touched.targetVer && formik.errors.targetVer}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        {!isNew ? (
          <Stack direction="row" spacing={1}>
            <Button color="warning" onClick={() => setConfirmAction('reissue')}>토큰 재발급</Button>
            <Button color="error" onClick={() => setConfirmAction('delete')}>삭제</Button>
          </Stack>
        ) : <span />}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>취소</Button>
          <Button variant="contained" onClick={formik.handleSubmit}>
            {isNew ? '등록' : '수정'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>

      {/* 삭제·토큰 재발급 확인 — 예전엔 수정 창 맨 아래(알림 설정 밑)에 경고를 붙여서, 창이 길면(특히 폰)
          화면 밖이라 안 보여 "삭제를 눌러도 반응이 없다"로 보였다(2026-10-19 운영 제보). 화면 가운데 작은 창으로 띄운다. */}
      <Dialog open={!isNew && Boolean(confirmAction)} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmAction === 'reissue' ? '토큰 재발급' : '에이전트 삭제'}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {confirmAction === 'reissue'
              ? '토큰을 재발급하면 이전 토큰은 즉시 사용할 수 없게 됩니다. 에이전트 PC에서 설정 파일을 새 토큰으로 바꿔야 다시 연결됩니다.'
              : `'${agentData?.agentNm || ''}' 에이전트를 삭제하면 담당하던 장비들은 담당 에이전트가 지정되지 않은 상태로 바뀝니다.`}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>취소</Button>
          <Button color="error" variant="contained" onClick={runConfirm}>
            {confirmAction === 'reissue' ? '재발급' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EqpAgentFormDialog;
